import { useState } from "react";
import { useAuth, apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Eye, EyeOff, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Human-friendly password age: "just now", "5 minutes ago", "3 hours ago",
 * "yesterday", "6 days ago", or an absolute date for anything older than a
 * week. Keeps the header line compact instead of showing a long ISO string.
 */
const formatPasswordDate = (iso) => {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diffMs = now - then;
  if (Number.isNaN(diffMs)) return "";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

/**
 * Settings → Security panel — lets any signed-in user set (first time) or
 * change (subsequent) their email/password credentials.
 *
 * Two modes based on `user.has_password` (populated by GET /auth/me):
 *   - false → "Set a password" — only new_password required. This is the
 *     Google-only or magic-link-only user's first time picking a password
 *     for faster future sign-ins.
 *   - true  → "Change your password" — current_password + new_password.
 */
export const SecurityPanel = () => {
  const { user, setUser } = useAuth();
  const hasPassword = Boolean(user?.has_password);
  const [forgotting, setForgotting] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const triggerForgot = async () => {
    if (!user?.email) {
      toast.error("Add an email in the Email panel first — password reset requires one.");
      return;
    }
    setForgotting(true);
    try {
      await apiClient.post("/auth/password/forgot", { email: user.email });
      toast.success(`Reset link sent to ${user.email}. Check your inbox and follow the link — no current password needed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email.");
    } finally {
      setForgotting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    if (hasPassword && !current) {
      toast.error("Enter your current password.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { new_password: next };
      if (hasPassword) payload.current_password = current;
      const res = await apiClient.post("/auth/password/set", payload);
      toast.success(hasPassword ? "Password updated." : "Password set — you can now sign in with email + password.");
      // Reflect the new state so the label flips to "Change your password"
      // on subsequent visits without needing a page refresh. Also stash
      // the new password_updated_at so the "last updated" line refreshes.
      if (setUser) {
        setUser((u) => (u ? {
          ...u,
          has_password: true,
          password_updated_at: res.data?.password_updated_at || new Date().toISOString(),
        } : u));
      }
      reset();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not update password. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl">
      <Card data-testid="security-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#0D9488]" />
            {hasPassword ? "Change your password" : "Set a password"}
          </CardTitle>
          <CardDescription>
            {hasPassword
              ? "Update your Nirvanta password. Signing you out of other devices after a successful change is coming soon — for now, use the reset flow if a device is lost."
              : "You currently sign in with Google or a magic link. Adding a password lets you sign in even faster with your password manager."}
            {hasPassword && user?.password_updated_at && (
              <span
                className="block mt-1.5 text-xs text-slate-500"
                data-testid="password-last-updated"
              >
                Last updated {formatPasswordDate(user.password_updated_at)}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {hasPassword && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="current-password">Current password</Label>
                  <button
                    type="button"
                    onClick={triggerForgot}
                    disabled={forgotting || !user?.email}
                    className="text-xs text-[#0D9488] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="security-forgot-current"
                    title={!user?.email ? "Add an email in the Email panel to enable password reset" : ""}
                  >
                    {forgotting ? "Sending…" : "Forgot your password?"}
                  </button>
                </div>
                <Input
                  id="current-password"
                  type={show ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  disabled={submitting}
                  data-testid="security-current-input"
                  autoComplete="current-password"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-password">New password (min 8 characters)</Label>
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
                  data-testid="security-show-toggle"
                >
                  {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {show ? "Hide" : "Show"}
                </button>
              </div>
              <Input
                id="new-password"
                type={show ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                disabled={submitting}
                data-testid="security-new-input"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                data-testid="security-confirm-input"
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                disabled={submitting || !next || !confirm || (hasPassword && !current)}
                data-testid="security-submit-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6] text-white"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                {submitting ? "Saving…" : hasPassword ? "Update password" : "Set password"}
              </Button>
              {hasPassword && (
                <p className="text-xs text-slate-500">
                  Signed in with Google &amp; magic link continue to work.
                </p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
